import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { ValidatorForm } from "react-material-ui-form-validator";
import RadioGroup from "@mui/material/RadioGroup";
import FormLayout from "../../../components/FormLayout";
import TextInput from "../../../components/TextInput";
import CustomRadio from "../../../components/CustomRadio";
import MaskTextInput from "../../../components/MaskTextInput";
import { MaskTypes } from "../../../utils/models";
import { connect } from "react-redux";
import { userSetupSlice, UserSetupStep, UserSetupState, UserSetupSubstep } from "../../../store/reducer/user_setup";
import { userSlice } from "../../../store/reducer/user";
import CustomButton from "../../../components/CustomButton";
import { push } from "redux-first-history";
import { User } from "../../../model/user";
import { defaultPersonalInfo, fallbackPhoneNumber } from "../../../model/personal_info";
import { Dispatch } from "redux";
import { useLocation } from "react-router-dom";
import { extractSSN } from "../../../utils/dataConverter";
import SpouseFilingPrompt from "./SpouseFilingPrompt";
import SSNReviewPrompt from "../../../components/SSNReviewPrompt";
import AccessibleFormControlLabel from "./AccessibleFormControlLabel";

interface PersonalInfoFormProps {
  user: User,
  userSetupState: UserSetupState,
  dispatch: Dispatch<any>,
}

const PersonalInfoForm = ({
  dispatch,
  user,
  userSetupState,
}: PersonalInfoFormProps) => {

  const location = useLocation();
  const personal = location.pathname === '/user/personal-info';

  const _setFormData = (update: User) => {
    dispatch(userSlice.actions.updateUserData(update));
  }

  const [displaySpouseFilingModal, setDisplaySpouseFilingModal] = useState(false);
  const [lockSpouseSSN, setLockSpouseSSN] = useState(false);
  const [showSSNReview, setShowSSNReview] = useState(false);
  
  // On initial load, reset frontend phone number for
  // certain edge case where we temporarily needed to send
  // a fallback phone number to backend during address verification.
  // See UserSetupSaga#verifyAddressSaga().
  useEffect(() => {
    let ignore = false;

    if (!ignore && user.phone_number == fallbackPhoneNumber) {
      dispatch(userSlice.actions.updateUserData({
        phone_number: defaultPersonalInfo.phone_number,
      }));
    }

    return () => {
      ignore = true;
    }
  }, []);

  useEffect(() => {
    if (!personal && user.spouse_social_security) {
      setLockSpouseSSN(true);
    }
  }, [dispatch, personal]);


  return (
    <FormLayout
      title={
        personal
          ? "Taxpayer"
          : "Your Spouse"
      }
      subtitle={
        personal
          ? "Personal Information"
          : "Joint Filing"
      }
      description={
        personal
          ? "Please enter your legal name and phone number below."
          : "Please enter your spouse’s information below."
      }
    >
      {personal ? (
        <ValidatorForm
          onSubmit={() => {
            dispatch(userSetupSlice.actions.clearSubstepErrors(UserSetupSubstep.PersonalInfo));
            dispatch(userSetupSlice.actions.confirmStep());
          }}
          onError={(_) => dispatch(userSetupSlice.actions.addSubstepError(UserSetupSubstep.PersonalInfo))}
          instantValidate={true}
        >
          <Grid container spacing={2} className='form-container xl noexpand'>
            <Grid className='form-row' size={6}>
              <TextInput
                aria-required
                name="first_name"
                label="First name"
                placeholder="First name"
                value={user.first_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  _setFormData({ first_name: e.target.value })
                }
                validators={["required"]}
                errorMessages={["First name is required"]}
              />
            </Grid>
            <Grid className='form-row' size={6}>
              <TextInput
                aria-required
                name="last_name"
                label="Last name"
                placeholder="Last name"
                value={user.last_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  _setFormData({ last_name: e.target.value })
                }
                validators={["required"]}
                errorMessages={["Last name is required"]}
              />
            </Grid>
            <Grid className='form-row' size={12}>
              <MaskTextInput
                variant="outlined"
                name="phone_number"
                aria-required
                label={"Phone Number"}
                value={user.phone_number}
                maskType={MaskTypes.PhoneNo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  _setFormData({ phone_number: e.target.value });
                  dispatch(userSetupSlice.actions.setPInfoPhoneNoValidationError(""));
                }}
                error={!!userSetupState.pInfoPhoneNoValidationError}
                helperText={userSetupState.pInfoPhoneNoValidationError}
              />
            </Grid>
            <Grid className='radio-group-row' size={12}>
              <span className='radio-group-label'>
                Would you like to file your Springboro return jointly with your
                spouse?
              </span>
              <RadioGroup
                value={user.is_jointfiling}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  // eslint-disable-next-line eqeqeq
                  const _jointFiling = e.target.value == "true";
                  _setFormData({ is_jointfiling: _jointFiling });
                  if (_jointFiling) {
                    setDisplaySpouseFilingModal(true);
                  }
                }}
              >
                <AccessibleFormControlLabel
                  value={true}
                  role="radio"
                  control={<CustomRadio tabIndex={-1} />}
                  label="Yes, I am filing with my spouse."
                />
                <AccessibleFormControlLabel
                  value={false}
                  role="radio"
                  control={<CustomRadio tabIndex={-1} />}
                  label="No, I am filing solo."
                />
              </RadioGroup>
            </Grid>
          </Grid>
          <div className='btn-container'>
            <CustomButton
              variant="outlined"
              disableElevation={false}
              label={"back"}
              onClick={() => dispatch(push('/user/welcome-back'))}
              buttonSize='btn-lg'
            />
            <CustomButton
              variant="contained"
              disableElevation={true}
              label={"next"}
              type="submit"
              buttonSize="btn-lg"
            />
          </div>
          <SpouseFilingPrompt show={displaySpouseFilingModal} onConfirm={() => setDisplaySpouseFilingModal(false)}/>
        </ValidatorForm>
      ) : (
        <ValidatorForm
          onSubmit={() => {
            setShowSSNReview(true);
          }}
          onError={(_) => dispatch(userSetupSlice.actions.addSubstepError(UserSetupSubstep.SpouseInfo))}
          instantValidate={true}
        >
          <Grid container spacing={2} className='form-container xl noexpand'>
            <Grid className='form-row' size={6}>
              <TextInput
                name="spouse_first_name"
                label="Spouse’s first name"
                placeholder="First name"
                value={user.spouse_first_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  _setFormData({ spouse_first_name: e.target.value })
                }
                validators={["required"]}
                errorMessages={["Spouse’s first name is required"]}
              />
            </Grid>
            <Grid className='form-row' size={6}>
              <TextInput
                name="spouse_last_name"
                label="Spouse’s last name"
                placeholder="Last name"
                value={user.spouse_last_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  _setFormData({ spouse_last_name: e.target.value })
                }
                validators={["required"]}
                errorMessages={["Spouse’s last name is required"]}
              />
            </Grid>
            <Grid className='form-row' size={12}>
              {lockSpouseSSN ? 
              <TextInput
                name="spouse_social_security"
                label="Spouse’s social security"
                disabled={true}
                value={user.spouse_social_security ?? ""}
              /> :
              <MaskTextInput
                variant="outlined"
                name="spouse_social_security"
                label={"Spouse’s social security"}
                value={user.spouse_social_security ?? ""} // Do not remove Null coalescing -> mask-text-input bug w/o
                maskType={MaskTypes.SSN}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  dispatch(userSetupSlice.actions.setSpouseSsnValidationError(""));
                  if (extractSSN(e.target.value)?.length === 9) {
                    console.log("Validating SSN");
                    dispatch(userSetupSlice.actions.validateSSN(e.target.value!));
                  }
                  _setFormData({ spouse_social_security: e.target.value });
                }}
                error={!!userSetupState.spouseSsnValidationError}
                helperText={userSetupState.spouseSsnValidationError}
              />}
            </Grid>
          </Grid>
          <div className='btn-container'>
            <CustomButton
              variant="outlined"
              disableElevation={false}
              label={"back"}
              onClick={() => {
                dispatch(push('/user/personal-info'));
              }}
              buttonSize='btn-lg'
            />
            <CustomButton
              variant="contained"
              disableElevation={true}
              label={"next"}
              disabled={userSetupState.disableNext 
                || extractSSN(user.spouse_social_security)?.length !== 9
                || !!userSetupState.spouseSsnValidationError}
              type="submit"
              buttonSize="btn-lg"
            />
          </div>
        </ValidatorForm>
      )}
      <SSNReviewPrompt
        show={showSSNReview}
        forSpouse={true}
        ssn={user.spouse_social_security ?? ""}
        onAccept={() => {
          setShowSSNReview(false);
          dispatch(userSetupSlice.actions.clearSubstepErrors(UserSetupSubstep.SpouseInfo));
          dispatch(userSetupSlice.actions.confirmStep());
        }}
        onCancel={() => setShowSSNReview(false)}
      />
    </FormLayout>
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    userSetupState: state.userSetup
  };
};

export default connect(mapStateToProps)(PersonalInfoForm);
